import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { cs, type Dict } from "./locales/cs";
import { en } from "./locales/en";
import { detectLocale, STORAGE_KEY, type Locale } from "./locale";
import { localePath, parsePath } from "../lib/routes";

export type { Locale } from "./locale";
export { INTL_LOCALE, LOCALES } from "./locale";

const DICTS: Record<Locale, Dict> = { cs, en };

type Params = Record<string, string | number>;

/** Dosadí {placeholder} hodnoty; chybějící klíč nechá viditelně v textu. */
function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    key in params ? String(params[key]) : `{${key}}`,
  );
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage nemusí být dostupný */
    }
    // Přepnutí jazyka přepíše i prefix v URL: odkaz zůstane sdílitelný ve
    // zvoleném jazyce a reload skončí tam, kde uživatel byl (v URL vyhrává
    // prefix, viz detectLocale).
    const { pathname, search, hash } = window.location;
    history.replaceState(null, "", localePath(next, parsePath(pathname).rest) + search + hash);
  }, []);

  // Drž <html lang> v souladu s UI (kvůli přístupnosti a vyhledávačům).
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

function useLocaleContext(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within <LocaleProvider>");
  return ctx;
}

/** Vrátí aktuální locale a setter pro přepínání jazyka. */
export function useLocale(): LocaleContextValue {
  return useLocaleContext();
}

/** Překladová funkce `t(key, params?)` vázaná na aktuální locale. */
export function useT() {
  const { locale } = useLocaleContext();
  return useCallback(
    (key: keyof Dict, params?: Params) => interpolate(DICTS[locale][key], params),
    [locale],
  );
}
